import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../lib/auth-context';
import { rechargeAPI } from '../lib/api';
import DashboardLayout from '../components/layout/DashboardLayout';
import { Card, CardContent } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '../components/ui/table';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { toast } from 'sonner';
import {
  Search,
  Eye,
  CheckCircle,
  XCircle,
  Clock,
  Download,
  RotateCcw,
  Loader2,
  Settings,
  IndianRupee,
  ChevronLeft,
  ChevronRight,
  Copy,
} from 'lucide-react';
import { format } from 'date-fns';
import { useSearchParams } from 'react-router-dom';

const RechargeHistory = () => {
  const { user } = useAuth();
  const [searchParams] = useSearchParams();
  const isAdmin = user?.role === 'admin';

  const [recharges, setRecharges] = useState([]);
  const [stats, setStats] = useState(null);
  const [pagination, setPagination] = useState({ total: 0, page: 1, limit: 50, total_pages: 1 });
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState(searchParams.get('status') || '');
  const [operatorFilter, setOperatorFilter] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [userFilter, setUserFilter] = useState('');
  const [pageSize, setPageSize] = useState(50);
  const [currentPage, setCurrentPage] = useState(1);

  const [selectedRecharge, setSelectedRecharge] = useState(null);
  const [detailsOpen, setDetailsOpen] = useState(false);
  const [checkingStatus, setCheckingStatus] = useState({});
  const [checkingAll, setCheckingAll] = useState(false);
  const [users, setUsers] = useState([]);

  // Manual status change
  const [statusDialogOpen, setStatusDialogOpen] = useState(false);
  const [statusChangeTarget, setStatusChangeTarget] = useState(null);
  const [newStatus, setNewStatus] = useState('');
  const [newOperatorId, setNewOperatorId] = useState('');
  const [statusChanging, setStatusChanging] = useState(false);

  // Complaint
  const [complaintOpen, setComplaintOpen] = useState(false);
  const [complaintTarget, setComplaintTarget] = useState(null);
  const [complaintType, setComplaintType] = useState('');
  const [complaintMessage, setComplaintMessage] = useState('');
  const [complaintSubmitting, setComplaintSubmitting] = useState(false);
  const [userComplaints, setUserComplaints] = useState({});

  const complaintOptions = [
    { value: 'not_received', label: 'Recharge not received' },
    { value: 'wrong_provider_id', label: 'Please provide correct provider ID' },
    { value: 'wrong_amount', label: 'Wrong amount recharged' },
    { value: 'other', label: 'Other' },
  ];

  const fetchRecharges = useCallback(async () => {
    try {
      const params = { limit: pageSize, page: currentPage };
      if (statusFilter) params.status = statusFilter;
      if (operatorFilter) params.operator = operatorFilter;
      if (startDate) params.start_date = startDate;
      if (endDate) params.end_date = endDate;
      if (userFilter) params.user_id = userFilter;
      const r = await rechargeAPI.getRecharges(params);
      const d = r.data;
      if (d.data) {
        setRecharges(d.data);
        setStats(d.stats);
        setPagination(d.pagination);
      } else {
        setRecharges(Array.isArray(d) ? d : []);
      }
    } catch (e) {
      toast.error('Failed to load recharges');
    }
    setLoading(false);
  }, [pageSize, currentPage, statusFilter, operatorFilter, startDate, endDate, userFilter]);

  useEffect(() => { fetchRecharges(); }, [fetchRecharges]);

  // Auto-refresh every 5 seconds
  useEffect(() => {
    const interval = setInterval(() => { fetchRecharges(); }, 5000);
    return () => clearInterval(interval);
  }, [fetchRecharges]);

  // Fetch users for filter
  useEffect(() => {
    if (isAdmin) {
      import('../lib/api').then(m => {
        m.default.get('/users').then(r => setUsers(r.data || [])).catch(() => {});
      });
    }
  }, [isAdmin]);

  // Fetch retailer's complaints to track which recharges already have complaints
  const fetchUserComplaints = useCallback(async () => {
    if (isAdmin) return;
    try {
      const m = await import('../lib/api');
      const r = await m.default.get('/complaints');
      const map = {};
      (r.data || []).forEach(c => {
        const refId = c.reference_id || c.recharge_id;
        if (refId) map[refId] = c;
      });
      setUserComplaints(map);
    } catch {}
  }, [isAdmin]);

  useEffect(() => { fetchUserComplaints(); }, [fetchUserComplaints]);

  const handleCheckStatus = async (r) => {
    setCheckingStatus(prev => ({ ...prev, [r.id]: true }));
    try {
      const res = await rechargeAPI.checkStatus(r.id);
      if (res.data?.changed) {
        toast.success(`Status: ${res.data.old_status} → ${res.data.new_status}`);
        fetchRecharges();
      } else {
        toast.info('Status unchanged');
      }
    } catch { toast.error('Check failed'); }
    setCheckingStatus(prev => ({ ...prev, [r.id]: false }));
  };

  const handleCheckAll = async () => {
    setCheckingAll(true);
    try {
      const res = await rechargeAPI.checkAllPending();
      toast.success(`Updated: ${res.data?.updated || 0} recharges`);
      fetchRecharges();
    } catch { toast.error('Batch check failed'); }
    setCheckingAll(false);
  };

  const handleExport = () => {
    const csv = [
      'ID,User,Phone,Service,Operator,Number,Amount,Commission,Cost,Closing,Status,OperatorRef,API,Date',
      ...recharges.map(r =>
        `${r.txn_id},"${r.user_name}",${r.user_phone || ''},${r.service},${r.operator},${r.mobile},${r.amount},${r.commission_amount},${r.cost},${r.closing_balance || ''},${r.status},${r.operator_id || ''},${r.api_name || ''},${r.created_at ? format(new Date(r.created_at), 'dd/MM/yy HH:mm') : ''}`
      ),
    ].join('\n');
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `recharge_report_${format(new Date(), 'yyyyMMdd')}.csv`; a.click();
    toast.success('Report downloaded');
  };

  const openStatusChange = (recharge) => {
    setStatusChangeTarget(recharge);
    setNewStatus(recharge.status);
    setNewOperatorId(recharge.operator_id || '');
    setStatusDialogOpen(true);
  };

  const handleManualStatusChange = async () => {
    if (!statusChangeTarget || !newStatus) return;
    setStatusChanging(true);
    try {
      const res = await rechargeAPI.manualStatusChange(statusChangeTarget.id, {
        status: newStatus,
        operator_id: newOperatorId || null,
      });
      toast.success(res.data?.message || 'Status updated');
      fetchRecharges();
      setStatusDialogOpen(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Status change failed');
    }
    setStatusChanging(false);
  };

  const openComplaint = (recharge) => {
    setComplaintTarget(recharge);
    setComplaintType('');
    setComplaintMessage('');
    setComplaintOpen(true);
  };

  const handleComplaintSubmit = async () => {
    if (!complaintTarget || !complaintType) { toast.error('Please select complaint type'); return; }
    const selectedLabel = complaintOptions.find(o => o.value === complaintType)?.label || complaintType;
    setComplaintSubmitting(true);
    try {
      await import('../lib/api').then(m =>
        m.default.post('/complaints', {
          subject: `Recharge Complaint - ${complaintTarget.mobile} (₹${complaintTarget.amount})`,
          message: `Recharge ID: ${complaintTarget.txn_id}\nMobile: ${complaintTarget.mobile}\nOperator: ${complaintTarget.operator}\nAmount: ₹${complaintTarget.amount}\nOperator Ref: ${complaintTarget.operator_id || 'N/A'}\nStatus: ${complaintTarget.status}\n\nComplaint Type: ${selectedLabel}\n${complaintMessage ? 'Details: ' + complaintMessage : ''}`,
          category: 'recharge_complaint',
          complaint_type: complaintType,
          reference_id: complaintTarget.id,
        })
      );
      toast.success('Complaint submitted successfully');
      setComplaintOpen(false);
      fetchUserComplaints();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to submit complaint');
    }
    setComplaintSubmitting(false);
  };

  const getStatusBadge = (status) => {
    const map = {
      success: <Badge className="bg-emerald-100 text-emerald-700 border-emerald-200 text-[10px] font-bold">SUCCESS</Badge>,
      failed: <Badge className="bg-red-100 text-red-700 border-red-200 text-[10px] font-bold">FAILURE</Badge>,
      pending: <Badge className="bg-amber-100 text-amber-700 border-amber-200 text-[10px] font-bold">PENDING</Badge>,
      refund: <Badge className="bg-blue-100 text-blue-700 border-blue-200 text-[10px] font-bold">REFUND</Badge>,
    };
    return map[status] || <Badge variant="outline" className="text-[10px]">{status?.toUpperCase()}</Badge>;
  };

  const filtered = recharges.filter(r => {
    if (!search) return true;
    const s = search.toLowerCase();
    return (r.mobile || '').includes(s) || (r.user_name || '').toLowerCase().includes(s) || (r.txn_id || '').toLowerCase().includes(s) || (r.operator_id || '').toLowerCase().includes(s);
  });

  return (
    <DashboardLayout>
      <div className="space-y-4" data-testid="recharge-history">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-xl font-bold text-foreground">{isAdmin ? 'Recharge Report' : 'Recharge History'}</h1>
            <p className="text-xs text-muted-foreground mt-0.5">View & manage all recharges</p>
          </div>
          <div className="flex gap-2">
            {isAdmin && (
              <Button onClick={handleCheckAll} disabled={checkingAll} className="bg-[#4680ff] hover:bg-[#2563eb] text-white text-xs h-8" data-testid="check-all-btn">
                {checkingAll ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" /> : <RotateCcw className="w-3.5 h-3.5 mr-1.5" />}
                Check All Pending
              </Button>
            )}
            <Button variant="outline" onClick={handleExport} className="text-xs h-8" data-testid="export-btn">
              <Download className="w-3.5 h-3.5 mr-1.5" /> Export CSV
            </Button>
          </div>
        </div>

        {/* Summary Stats */}
        {stats && (
          <div className="grid grid-cols-3 gap-3" data-testid="recharge-stats">
            <Card className="border-emerald-200 bg-emerald-50/50">
              <CardContent className="p-3">
                <p className="text-[10px] font-bold text-emerald-600 uppercase tracking-wider">Success</p>
                <p className="text-lg font-black text-emerald-700">
                  <span className="font-mono">&#8377;{stats.success_amount?.toLocaleString('en-IN')}</span>
                  <span className="text-xs font-normal ml-1.5">({stats.success_count})</span>
                </p>
                <p className="text-[10px] text-emerald-600 mt-0.5">
                  Comm: <span className="font-bold font-mono">&#8377;{stats.total_commission?.toFixed(2)}</span>
                </p>
              </CardContent>
            </Card>
            <Card className="border-red-200 bg-red-50/50">
              <CardContent className="p-3">
                <p className="text-[10px] font-bold text-red-600 uppercase tracking-wider">Failure</p>
                <p className="text-lg font-black text-red-700">
                  <span className="font-mono">&#8377;{stats.failed_amount?.toLocaleString('en-IN')}</span>
                  <span className="text-xs font-normal ml-1.5">({stats.failed_count})</span>
                </p>
              </CardContent>
            </Card>
            <Card className="border-amber-200 bg-amber-50/50">
              <CardContent className="p-3">
                <p className="text-[10px] font-bold text-amber-600 uppercase tracking-wider">Pending</p>
                <p className="text-lg font-black text-amber-700">
                  <span className="font-mono">&#8377;{stats.pending_amount?.toLocaleString('en-IN')}</span>
                  <span className="text-xs font-normal ml-1.5">({stats.pending_count})</span>
                </p>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Filters */}
        <Card>
          <CardContent className="p-3">
            <div className="flex flex-wrap items-center gap-2">
              <div className="relative flex-1 min-w-[180px]">
                <Search className="absolute left-2.5 top-2 h-3.5 w-3.5 text-muted-foreground" />
                <Input placeholder="Search mobile, name, txn..." value={search} onChange={e => setSearch(e.target.value)} className="pl-8 h-8 text-xs" data-testid="search-input" />
              </div>
              <Select value={statusFilter} onValueChange={v => { setStatusFilter(v === 'all' ? '' : v); setCurrentPage(1); }}>
                <SelectTrigger className="w-[120px] h-8 text-xs" data-testid="status-filter">
                  <SelectValue placeholder="All Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="success">Success</SelectItem>
                  <SelectItem value="failed">Failed</SelectItem>
                  <SelectItem value="pending">Pending</SelectItem>
                  <SelectItem value="refund">Refund</SelectItem>
                </SelectContent>
              </Select>
              <Select value={operatorFilter} onValueChange={v => { setOperatorFilter(v === 'all' ? '' : v); setCurrentPage(1); }}>
                <SelectTrigger className="w-[130px] h-8 text-xs" data-testid="operator-filter">
                  <SelectValue placeholder="All Operators" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Operators</SelectItem>
                  <SelectItem value="jio">JIO</SelectItem>
                  <SelectItem value="airtel">Airtel</SelectItem>
                  <SelectItem value="vi">VI</SelectItem>
                  <SelectItem value="bsnl_topup">BSNL</SelectItem>
                </SelectContent>
              </Select>
              <Input type="date" value={startDate} onChange={e => { setStartDate(e.target.value); setCurrentPage(1); }} className="w-[130px] h-8 text-xs" />
              <Input type="date" value={endDate} onChange={e => { setEndDate(e.target.value); setCurrentPage(1); }} className="w-[130px] h-8 text-xs" />
              {isAdmin && (
                <Select value={userFilter} onValueChange={v => { setUserFilter(v === 'all' ? '' : v); setCurrentPage(1); }}>
                  <SelectTrigger className="w-[140px] h-8 text-xs">
                    <SelectValue placeholder="All Users" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Users</SelectItem>
                    {users.map(u => <SelectItem key={u.id} value={u.id}>{u.name}</SelectItem>)}
                  </SelectContent>
                </Select>
              )}
            </div>
          </CardContent>
        </Card>

        {/* Pagination top */}
        <div className="flex items-center justify-between">
          <p className="text-xs text-muted-foreground font-semibold">
            Transactions ({pagination.total})
          </p>
          <div className="flex items-center gap-2">
            <span className="text-xs text-muted-foreground">PageSize:</span>
            <Select value={String(pageSize)} onValueChange={v => { setPageSize(Number(v)); setCurrentPage(1); }}>
              <SelectTrigger className="w-[70px] h-7 text-xs" data-testid="page-size">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="25">25</SelectItem>
                <SelectItem value="50">50</SelectItem>
                <SelectItem value="100">100</SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        {/* Table */}
        <Card>
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-[#1b2838] hover:bg-[#1b2838]">
                    <TableHead className="text-white text-[10px] font-bold py-2 px-2">ID</TableHead>
                    <TableHead className="text-white text-[10px] font-bold py-2 px-2">User</TableHead>
                    <TableHead className="text-white text-[10px] font-bold py-2 px-2">Service</TableHead>
                    <TableHead className="text-white text-[10px] font-bold py-2 px-2">Operator</TableHead>
                    <TableHead className="text-white text-[10px] font-bold py-2 px-2">Comm</TableHead>
                    <TableHead className="text-white text-[10px] font-bold py-2 px-2">Number</TableHead>
                    <TableHead className="text-white text-[10px] font-bold py-2 px-2">Amount</TableHead>
                    <TableHead className="text-white text-[10px] font-bold py-2 px-2">Cost</TableHead>
                    <TableHead className="text-white text-[10px] font-bold py-2 px-2">Closing</TableHead>
                    <TableHead className="text-white text-[10px] font-bold py-2 px-2">Date</TableHead>
                    <TableHead className="text-white text-[10px] font-bold py-2 px-2">Status</TableHead>
                    <TableHead className="text-white text-[10px] font-bold py-2 px-2">OperatorRef</TableHead>
                    {isAdmin && <TableHead className="text-white text-[10px] font-bold py-2 px-2">API</TableHead>}
                    <TableHead className="text-white text-[10px] font-bold py-2 px-2">{isAdmin ? 'Action' : ''}</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {loading ? (
                    <TableRow><TableCell colSpan={14} className="text-center py-8"><Loader2 className="w-5 h-5 animate-spin mx-auto" /></TableCell></TableRow>
                  ) : filtered.length === 0 ? (
                    <TableRow><TableCell colSpan={14} className="text-center py-8 text-muted-foreground text-xs">No recharges found</TableCell></TableRow>
                  ) : (
                    filtered.map((r, i) => (
                      <TableRow key={r.id} className={i % 2 === 0 ? 'bg-white' : 'bg-slate-50/50'} data-testid={`recharge-row-${r.id}`}>
                        <TableCell className="text-[10px] py-2 px-2 font-mono text-muted-foreground">{r.txn_id?.slice(-8) || '-'}</TableCell>
                        <TableCell className="text-[10px] py-2 px-2">
                          <div className="font-semibold">{r.user_name}</div>
                          {r.user_phone && <div className="text-muted-foreground">[{r.user_phone}]</div>}
                        </TableCell>
                        <TableCell className="text-[10px] py-2 px-2 capitalize">{r.service}</TableCell>
                        <TableCell className="text-[10px] py-2 px-2 font-semibold uppercase">{r.operator}</TableCell>
                        <TableCell className="text-[10px] py-2 px-2">
                          <div className="font-mono font-semibold text-emerald-600">{r.commission_amount?.toFixed(3) || '0.000'}</div>
                          <div className="text-muted-foreground">[{r.commission_rate}{r.commission_type === 'percentage' ? '%' : ''}]</div>
                        </TableCell>
                        <TableCell className="text-[10px] py-2 px-2 font-mono">{r.mobile}</TableCell>
                        <TableCell className="text-[10px] py-2 px-2 font-mono font-semibold">{r.amount?.toFixed(3)}</TableCell>
                        <TableCell className="text-[10px] py-2 px-2 font-mono">{r.cost?.toFixed(3)}</TableCell>
                        <TableCell className="text-[10px] py-2 px-2 font-mono">{r.closing_balance?.toFixed(3) || '-'}</TableCell>
                        <TableCell className="text-[10px] py-2 px-2 whitespace-nowrap">
                          {r.created_at ? format(new Date(r.created_at), 'dd/MM/yy') : '-'}
                          <div className="text-muted-foreground">{r.created_at ? format(new Date(r.created_at), 'hh:mm:ss a') : ''}</div>
                        </TableCell>
                        <TableCell className="text-[10px] py-2 px-2">{getStatusBadge(r.status)}</TableCell>
                        <TableCell className="text-[10px] py-2 px-2 font-mono">
                          {r.operator_id ? (
                            <div className="flex items-center gap-1">
                              {r.operator?.toLowerCase() === 'google_play' || r.operator?.toLowerCase() === 'google play' ? (
                                <span className="text-blue-600 font-semibold" title="Google Redeem Code">{r.operator_id}</span>
                              ) : <span>{r.operator_id}</span>}
                              <button
                                type="button"
                                onClick={() => { navigator.clipboard.writeText(r.operator_id); toast.success('Copied!'); }}
                                className="p-0.5 rounded hover:bg-slate-200 transition-colors flex-shrink-0"
                                data-testid={`copy-opid-${r.id}`}
                                title="Copy"
                              >
                                <Copy className="w-3 h-3 text-slate-400 hover:text-slate-600" />
                              </button>
                            </div>
                          ) : '-'}
                        </TableCell>
                        {isAdmin && <TableCell className="text-[10px] py-2 px-2 max-w-[100px] truncate" title={r.api_name}>{r.api_name || '-'}</TableCell>}
                        <TableCell className="text-[10px] py-2 px-2">
                          <div className="flex items-center gap-0.5">
                            {r.status === 'pending' && (
                              <Button variant="ghost" size="sm" className="h-6 w-6 p-0 text-blue-600" onClick={() => handleCheckStatus(r)} disabled={checkingStatus[r.id]} data-testid={`check-${r.id}`}>
                                {checkingStatus[r.id] ? <Loader2 className="w-3 h-3 animate-spin" /> : <RotateCcw className="w-3 h-3" />}
                              </Button>
                            )}
                            {isAdmin && (
                              <Button variant="ghost" size="sm" className="h-6 w-6 p-0 text-violet-600" onClick={() => openStatusChange(r)} data-testid={`action-${r.id}`} title="Change Status">
                                <Settings className="w-3 h-3" />
                              </Button>
                            )}
                            {!isAdmin && r.status === 'success' && userComplaints[r.id] && (
                              <Badge className={
                                userComplaints[r.id].status === 'resolved' ? 'bg-emerald-100 text-emerald-700 border-emerald-200 text-[8px] cursor-default' :
                                userComplaints[r.id].status === 'in_progress' ? 'bg-amber-100 text-amber-700 border-amber-200 text-[8px] cursor-default' :
                                'bg-red-100 text-red-700 border-red-200 text-[8px] cursor-default'
                              } data-testid={`complaint-status-${r.id}`}>
                                {userComplaints[r.id].status === 'resolved' ? 'Resolved' : userComplaints[r.id].status === 'in_progress' ? 'In Progress' : 'Complaint Filed'}
                              </Badge>
                            )}
                            {!isAdmin && r.status === 'success' && !userComplaints[r.id] && (() => {
                              const mins = r.created_at ? (Date.now() - new Date(r.created_at).getTime()) / 60000 : 999;
                              return mins >= 10;
                            })() && (
                              <Button variant="ghost" size="sm" className="h-6 px-1.5 text-[9px] text-orange-600 hover:text-orange-700 hover:bg-orange-50" onClick={() => openComplaint(r)} data-testid={`complaint-${r.id}`}>
                                Complaint
                              </Button>
                            )}
                            {!isAdmin && r.status === 'success' && !userComplaints[r.id] && (() => {
                              const mins = r.created_at ? (Date.now() - new Date(r.created_at).getTime()) / 60000 : 999;
                              return mins < 10;
                            })() && (
                              <span className="text-[8px] text-muted-foreground px-1">Wait {Math.ceil(10 - ((Date.now() - new Date(r.created_at).getTime()) / 60000))}m</span>
                            )}
                            {isAdmin && (
                              <Button variant="ghost" size="sm" className="h-6 w-6 p-0" onClick={() => { setSelectedRecharge(r); setDetailsOpen(true); }} data-testid={`detail-${r.id}`}>
                                <Eye className="w-3 h-3" />
                              </Button>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    ))
                  )}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>

        {/* Pagination bottom */}
        {pagination.total_pages > 1 && (
          <div className="flex items-center justify-center gap-1" data-testid="pagination">
            <Button variant="outline" size="sm" className="h-7 w-7 p-0" disabled={currentPage <= 1} onClick={() => setCurrentPage(p => p - 1)}>
              <ChevronLeft className="w-3.5 h-3.5" />
            </Button>
            {Array.from({ length: Math.min(pagination.total_pages, 7) }, (_, i) => {
              let pageNum;
              if (pagination.total_pages <= 7) {
                pageNum = i + 1;
              } else if (currentPage <= 4) {
                pageNum = i + 1;
              } else if (currentPage >= pagination.total_pages - 3) {
                pageNum = pagination.total_pages - 6 + i;
              } else {
                pageNum = currentPage - 3 + i;
              }
              return (
                <Button
                  key={pageNum}
                  variant={currentPage === pageNum ? 'default' : 'outline'}
                  size="sm"
                  className={`h-7 w-7 p-0 text-xs ${currentPage === pageNum ? 'bg-[#4680ff] text-white' : ''}`}
                  onClick={() => setCurrentPage(pageNum)}
                >
                  {pageNum}
                </Button>
              );
            })}
            <Button variant="outline" size="sm" className="h-7 w-7 p-0" disabled={currentPage >= pagination.total_pages} onClick={() => setCurrentPage(p => p + 1)}>
              <ChevronRight className="w-3.5 h-3.5" />
            </Button>
          </div>
        )}

        {/* Details Dialog */}
        <Dialog open={detailsOpen} onOpenChange={setDetailsOpen}>
          <DialogContent className="max-w-md" data-testid="recharge-details-dialog">
            <DialogHeader>
              <DialogTitle>Recharge Details</DialogTitle>
              <DialogDescription>{selectedRecharge?.txn_id}</DialogDescription>
            </DialogHeader>
            {selectedRecharge && (
              <div className="space-y-2.5 text-sm">
                <div className="grid grid-cols-2 gap-2 text-xs">
                  <div className="p-2 bg-muted rounded"><span className="text-muted-foreground block text-[10px]">Mobile</span><span className="font-mono font-bold">{selectedRecharge.mobile}</span></div>
                  <div className="p-2 bg-muted rounded"><span className="text-muted-foreground block text-[10px]">Amount</span><span className="font-mono font-bold">&#8377;{selectedRecharge.amount}</span></div>
                  <div className="p-2 bg-muted rounded"><span className="text-muted-foreground block text-[10px]">Operator</span><span className="font-bold uppercase">{selectedRecharge.operator}</span></div>
                  <div className="p-2 bg-muted rounded"><span className="text-muted-foreground block text-[10px]">Status</span>{getStatusBadge(selectedRecharge.status)}</div>
                  <div className="p-2 bg-muted rounded"><span className="text-muted-foreground block text-[10px]">Service</span><span className="capitalize">{selectedRecharge.service}</span></div>
                  <div className="p-2 bg-muted rounded"><span className="text-muted-foreground block text-[10px]">Commission</span><span className="font-mono text-emerald-600">&#8377;{selectedRecharge.commission_amount?.toFixed(3)} [{selectedRecharge.commission_rate}%]</span></div>
                  <div className="p-2 bg-muted rounded"><span className="text-muted-foreground block text-[10px]">Cost</span><span className="font-mono">&#8377;{selectedRecharge.cost?.toFixed(3)}</span></div>
                  <div className="p-2 bg-muted rounded"><span className="text-muted-foreground block text-[10px]">Closing</span><span className="font-mono">&#8377;{selectedRecharge.closing_balance?.toFixed(3) || '-'}</span></div>
                  <div className="p-2 bg-muted rounded"><span className="text-muted-foreground block text-[10px]">User</span><span>{selectedRecharge.user_name}</span></div>
                  <div className="p-2 bg-muted rounded"><span className="text-muted-foreground block text-[10px]">{selectedRecharge?.operator?.toLowerCase().includes('google') ? 'Redeem Code' : 'Operator Ref'}</span><div className="flex items-center gap-1"><span className="font-mono text-xs">{selectedRecharge.operator_id || '-'}</span>{selectedRecharge.operator_id && <button type="button" onClick={() => { navigator.clipboard.writeText(selectedRecharge.operator_id); toast.success('Copied!'); }} className="p-0.5 rounded hover:bg-slate-300 transition-colors" data-testid="copy-detail-opid" title="Copy"><Copy className="w-3 h-3 text-muted-foreground" /></button>}</div></div>
                  <div className="p-2 bg-muted rounded col-span-2"><span className="text-muted-foreground block text-[10px]">Client ID</span><span className="font-mono text-xs">{selectedRecharge.client_id || '-'}</span></div>
                  <div className="p-2 bg-muted rounded col-span-2"><span className="text-muted-foreground block text-[10px]">API</span><span className="text-xs">{selectedRecharge.api_name || '-'}</span></div>
                  <div className="p-2 bg-muted rounded col-span-2"><span className="text-muted-foreground block text-[10px]">Date</span><span>{selectedRecharge.created_at ? format(new Date(selectedRecharge.created_at), 'dd/MM/yyyy hh:mm:ss a') : '-'}</span></div>
                </div>
                {selectedRecharge.api_response && (
                  <div className="p-2 bg-muted rounded">
                    <span className="text-muted-foreground block text-[10px] mb-1">API Response</span>
                    <code className="text-[10px] break-all">{selectedRecharge.api_response}</code>
                  </div>
                )}
                {isAdmin && (
                  <DialogFooter>
                    <Button onClick={() => { setDetailsOpen(false); openStatusChange(selectedRecharge); }} variant="outline" className="w-full text-violet-600 border-violet-200" data-testid="manual-status-btn">
                      <Settings className="w-4 h-4 mr-2" /> Change Status / Operator ID
                    </Button>
                  </DialogFooter>
                )}
              </div>
            )}
          </DialogContent>
        </Dialog>

        {/* Manual Status Change Dialog */}
        <Dialog open={statusDialogOpen} onOpenChange={setStatusDialogOpen}>
          <DialogContent className="max-w-sm" data-testid="manual-status-dialog">
            <DialogHeader>
              <DialogTitle>Manual Status Change</DialogTitle>
              <DialogDescription>
                {statusChangeTarget && <span>{statusChangeTarget.mobile} | &#8377;{statusChangeTarget.amount} | {statusChangeTarget.operator}</span>}
              </DialogDescription>
            </DialogHeader>
            {statusChangeTarget && (
              <div className="space-y-4">
                <div className="flex items-center gap-2 p-3 bg-muted rounded-lg text-sm">
                  <span className="text-muted-foreground">Current:</span>
                  {getStatusBadge(statusChangeTarget.status)}
                </div>
                <div className="space-y-2">
                  <Label>New Status</Label>
                  <Select value={newStatus} onValueChange={setNewStatus}>
                    <SelectTrigger data-testid="new-status-select"><SelectValue placeholder="Select status" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="success"><span className="flex items-center gap-2"><CheckCircle className="w-3.5 h-3.5 text-emerald-500" />Success</span></SelectItem>
                      <SelectItem value="failed"><span className="flex items-center gap-2"><XCircle className="w-3.5 h-3.5 text-red-500" />Failed / Reject</span></SelectItem>
                      <SelectItem value="pending"><span className="flex items-center gap-2"><Clock className="w-3.5 h-3.5 text-amber-500" />Pending</span></SelectItem>
                      <SelectItem value="refund"><span className="flex items-center gap-2"><RotateCcw className="w-3.5 h-3.5 text-blue-500" />Refund</span></SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label>Operator ID</Label>
                  <Input value={newOperatorId} onChange={e => setNewOperatorId(e.target.value)} placeholder="Enter operator transaction ID" data-testid="operator-id-input" />
                </div>
                {newStatus === 'success' && statusChangeTarget.status !== 'success' && ['failed', 'refund'].includes(statusChangeTarget.status) && (
                  <div className="p-3 bg-emerald-50 border border-emerald-200 rounded-lg text-xs text-emerald-700 space-y-1">
                    <div><IndianRupee className="w-3.5 h-3.5 inline mr-1" /> &#8377;{statusChangeTarget.amount} will be <strong>deducted</strong> from wallet</div>
                    <div><CheckCircle className="w-3.5 h-3.5 inline mr-1" /> Commission will be added to wallet chain</div>
                  </div>
                )}
                {newStatus === 'success' && statusChangeTarget.status === 'pending' && (
                  <div className="p-3 bg-emerald-50 border border-emerald-200 rounded-lg text-xs text-emerald-700">
                    <CheckCircle className="w-3.5 h-3.5 inline mr-1" /> Commission will be added to wallet chain
                  </div>
                )}
                {(newStatus === 'failed' || newStatus === 'refund') && statusChangeTarget.status === 'success' && (
                  <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-xs text-red-700 space-y-1">
                    <div><IndianRupee className="w-3.5 h-3.5 inline mr-1" /> Commission will be <strong>reversed</strong> (deducted from wallets)</div>
                    <div><IndianRupee className="w-3.5 h-3.5 inline mr-1" /> &#8377;{statusChangeTarget.amount} will be <strong>refunded</strong> to retailer wallet</div>
                  </div>
                )}
                {(newStatus === 'failed' || newStatus === 'refund') && statusChangeTarget.status !== 'success' && !['failed', 'refund'].includes(statusChangeTarget.status) && (
                  <div className="p-3 bg-amber-50 border border-amber-200 rounded-lg text-xs text-amber-700">
                    <IndianRupee className="w-3.5 h-3.5 inline mr-1" /> &#8377;{statusChangeTarget.amount} will be refunded to wallet
                  </div>
                )}
                <DialogFooter>
                  <Button variant="outline" onClick={() => setStatusDialogOpen(false)}>Cancel</Button>
                  <Button onClick={handleManualStatusChange} disabled={statusChanging || newStatus === statusChangeTarget.status} className="bg-[#4680ff] hover:bg-[#2563eb] text-white" data-testid="confirm-status-change-btn">
                    {statusChanging ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <Settings className="w-4 h-4 mr-2" />}
                    {statusChanging ? 'Updating...' : 'Update Status'}
                  </Button>
                </DialogFooter>
              </div>
            )}
          </DialogContent>
        </Dialog>
        {/* Complaint Dialog (Retailer) */}
        <Dialog open={complaintOpen} onOpenChange={setComplaintOpen}>
          <DialogContent className="max-w-sm" data-testid="complaint-dialog">
            <DialogHeader>
              <DialogTitle>Submit Complaint</DialogTitle>
              <DialogDescription>
                {complaintTarget && <span>{complaintTarget.mobile} | &#8377;{complaintTarget.amount} | {complaintTarget.operator}</span>}
              </DialogDescription>
            </DialogHeader>
            {complaintTarget && (
              <div className="space-y-4">
                <div className="grid grid-cols-2 gap-2 text-xs">
                  <div className="p-2 bg-muted rounded"><span className="text-muted-foreground block text-[10px]">TXN ID</span><code className="text-[10px]">{complaintTarget.txn_id}</code></div>
                  <div className="p-2 bg-muted rounded"><span className="text-muted-foreground block text-[10px]">Status</span>{getStatusBadge(complaintTarget.status)}</div>
                  <div className="p-2 bg-muted rounded"><span className="text-muted-foreground block text-[10px]">Operator Ref</span><code className="text-[10px]">{complaintTarget.operator_id || 'N/A'}</code></div>
                  <div className="p-2 bg-muted rounded"><span className="text-muted-foreground block text-[10px]">Amount</span><span className="font-mono font-bold">&#8377;{complaintTarget.amount}</span></div>
                </div>
                <div className="space-y-2">
                  <Label>Complaint Type</Label>
                  <Select value={complaintType} onValueChange={setComplaintType}>
                    <SelectTrigger data-testid="complaint-type-select"><SelectValue placeholder="Select complaint type" /></SelectTrigger>
                    <SelectContent>
                      {complaintOptions.map(o => (
                        <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                {complaintType === 'other' && (
                  <div className="space-y-2">
                    <Label>Details</Label>
                    <textarea
                      value={complaintMessage}
                      onChange={e => setComplaintMessage(e.target.value)}
                      placeholder="Describe your issue..."
                      className="w-full min-h-[60px] rounded-md border border-input bg-background px-3 py-2 text-sm"
                      data-testid="complaint-message"
                    />
                  </div>
                )}
                <DialogFooter>
                  <Button variant="outline" onClick={() => setComplaintOpen(false)}>Cancel</Button>
                  <Button onClick={handleComplaintSubmit} disabled={complaintSubmitting || !complaintType} className="bg-orange-500 hover:bg-orange-600 text-white" data-testid="submit-complaint-btn">
                    {complaintSubmitting ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                    {complaintSubmitting ? 'Submitting...' : 'Submit Complaint'}
                  </Button>
                </DialogFooter>
              </div>
            )}
          </DialogContent>
        </Dialog>
      </div>
    </DashboardLayout>
  );
};

export default RechargeHistory;
